/**
 * In-memory cache of gallery preview images for the current tab session.
 *
 * SharePoint regenerates thumbnail renditions asynchronously: a save uploads the
 * client PNG to `thumbnails/0/source/content`, but the gallery reads
 * `thumbnails/0/large/content`, which keeps serving the previous rendition until
 * the rendition worker catches up. The client already holds the exact bytes it
 * just uploaded, so we keep them for the rest of the session and use them to
 * render the card immediately.
 *
 * This is deliberately memory-only. Preview bytes are customer content, and
 * persisting them (localStorage, IndexedDB) would create a durable at-rest copy
 * outside the M365 compliance boundary, with no way to honour access revocation
 * or deletion requests on that device. Holding them in the tab's heap keeps this
 * within the "processed in memory" classification every other data element in
 * the privacy assessment already uses, and the cache disappears on reload.
 */

const MAX_ENTRIES = 50;

interface CachedPreview {
  updatedAt: string;
  blob: Blob;
}

const previews = new Map<string, CachedPreview>();

/**
 * Return the cached preview only when it corresponds to exactly the version the
 * caller expects. Equality, never ordering: any difference means the drawing was
 * written somewhere else and the cached image must not be trusted.
 */
export function getPreview(drawingId: string, updatedAt: string): Blob | undefined {
  const entry = previews.get(drawingId);
  if (!entry) return undefined;
  if (entry.updatedAt !== updatedAt) {
    previews.delete(drawingId);
    return undefined;
  }
  return entry.blob;
}

function setPreview(drawingId: string, updatedAt: string, blob: Blob): void {
  // Re-insert so Map iteration order tracks recency for eviction.
  previews.delete(drawingId);
  previews.set(drawingId, { updatedAt, blob });
  while (previews.size > MAX_ENTRIES) {
    const oldest = previews.keys().next();
    if (oldest.done) break;
    previews.delete(oldest.value);
  }
}

function base64ToBlob(base64: string): Blob | null {
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: "image/png" });
  } catch {
    return null;
  }
}

/**
 * Cache the preview a save just produced, stamped with the `updatedAt` returned
 * by that same save. Save paths hold the image as base64 because that is the
 * wire format the backend expects.
 */
export function cachePreviewFromBase64(drawingId: string, updatedAt: string, base64: string): void {
  if (!drawingId || !updatedAt || !base64) return;
  const blob = base64ToBlob(base64);
  if (blob) setPreview(drawingId, updatedAt, blob);
}

/** Drop a single cached preview, e.g. when its drawing is deleted. */
export function deletePreview(drawingId: string): void {
  previews.delete(drawingId);
}

/** Drop every cached preview. Called on sign-out alongside the gallery cache. */
export function clearPreviewCache(): void {
  previews.clear();
}
